/// <amd-module name="Search/Wiki/Components/FilterPane" />
import * as React from "react";
import * as Container from "Search/Shared/Base/Container";
import * as SearchSharedContracts from "Search/WebApi/Contracts/Search.Shared";
import * as SharedResources from "Search/Shared/Resources";
import * as Resources from "Search/Wiki/Resources";
import * as SearchPickList from "SearchUI/SearchPickList";
import { format } from "VSS/Core/Util/String";
import { FilterType, IFilterStoreState } from "Search/Shared/Base/Stores/FilterStore";
import { compare } from "Search/Shared/Utils";
import { SearchConstants } from "Search/Wiki/Constants";
import { FilterBar } from "SearchUI/FilterBar";
import { ISearchStoreState } from "Search/Shared/Base/Stores/SearchStore2";
import { ActionCreator } from "Search/Wiki/Flux/ActionCreator";
import { StoreName, StoresHub, IAggregatedState } from "Search/Wiki/Flux/StoresHub";

const searchPickListMenuMaxWidth: number = 510;

export interface IFilterPaneProps extends Container.IContainerProps<StoreName, ActionCreator, IAggregatedState, StoresHub> {
    searchStoreState: ISearchStoreState<SearchSharedContracts.WikiSearchRequest, SearchSharedContracts.WikiSearchResponse>;
    filterStoreState: IFilterStoreState;
    isProjectContext: boolean;
}

export const FilterPane = (props: IFilterPaneProps): JSX.Element => {
    const { filter, filterItems } = props.filterStoreState;
    const { query, response } = props.searchStoreState;

    return (
        <>
            <FilterBar
                filter={filter}
                isProjectContext={props.isProjectContext} >
                {
                    Object
                        .keys(filterItems)
                        .map((key: string) => {
                            if (filterItems[key].filterType === FilterType.PickList &&
                                filterItems[key].visible) {
                                const items: SearchPickList.ISearchPickListItem[] = [];
                                if (filterItems[key].enabled) {
                                    const facets = response && response.facets[key];
                                    if (facets) {
                                        facets.map((facet: SearchSharedContracts.Filter) => {
                                            items.push({
                                                id: facet.id,
                                                name: facet.name,
                                                resultCount: facet.resultCount,
                                                selected: query.filters && query.filters[key] && query.filters[key].indexOf(facet.name) > -1
                                            });
                                        });
                                    }
                                }

                                return <SearchPickList.SearchPickListFilterBarItem
                                    key={key}
                                    lightWeightDisplay={true}
                                    filterItemKey={key}
                                    disableAnnounce={true}
                                    menuMaxWidth={searchPickListMenuMaxWidth}
                                    isExpandable={true}
                                    {...searchPickListPropCreators[key](cloneItems(items), filterItems[key].enabled)} />;
                            }
                        })
                }
            </FilterBar>
            <ResultsFetched searchResponse={response} />
        </>);
};

const ResultsFetched = (props: { searchResponse: SearchSharedContracts.WikiSearchResponse | undefined }): JSX.Element | null => {
    if (!props.searchResponse) {
        return null;
    }

    const { results, count } = props.searchResponse;

    if (!results || count == 0) {
        return null;
    }

    let displayText: string = "";
    if (results.length > 0) {
        if (count > results.length) {
            displayText = format(Resources.ShowingXofYResultsTitle, results.length, count);
        } else if (results.length === 1) {
            displayText = format(Resources.ShowingSingleResultTitle);
        } else {
            displayText = format(Resources.ShowingXResultsTitle, results.length);
        }

        return (
            <div className="wiki-results-info">
                {displayText}
            </div>
        );
    }

    return null;
}

function cloneItems(items: SearchPickList.ISearchPickListItem[]): SearchPickList.ISearchPickListItem[] {
    const clonedItems: SearchPickList.ISearchPickListItem[] = [];
    items.forEach(item => clonedItems.push(Object.assign({}, item)));

    return clonedItems;
}

function getFooterMessage(
    itemCount: number,
    searchText: string,
    showingSingleItemText: string,
    showingMultipleItemsText: string,
    foundSingleItemText: string,
    foundMultipleItemsText: string): string {
    const isSearchTextPresent = !!searchText && searchText !== "";
    itemCount = !isSearchTextPresent ? itemCount - 1 : itemCount; // Adjust for "All" item.

    return !isSearchTextPresent
        ? (itemCount > 1 ? format(showingMultipleItemsText, itemCount) : showingSingleItemText)
        : (itemCount > 1
            ? format(foundMultipleItemsText, itemCount)
            : itemCount === 0 ? format(SharedResources.NoResultsForSearchText, searchText) : foundSingleItemText);
}

function filterItems(items: SearchPickList.ISearchPickListItem[], searchText: string): SearchPickList.ISearchPickListItem[] {
    searchText = searchText.toLowerCase();
    return items
        .filter((item) => item.name.toLowerCase().indexOf(searchText) >= 0)
        .sort((a, b) => compare(a.name, b.name, searchText));
}

const searchPickListPropCreators = {
    [SearchConstants.ProjectFilterNew]:
        (items: SearchPickList.ISearchPickListItem[], enabled: boolean): SearchPickList.ISearchPickListProps => ({
            name: SearchConstants.ProjectFilterNew,
            items: items,
            allItemDisplayName: SharedResources.AllProjectsDisplayLabel,
            allItemLabel: SharedResources.All,
            width: 160,
            enabled: enabled,
            displayName: SharedResources.ProjectsDisplayLabel,
            searchTextPlaceholder: SharedResources.FindProjectPlaceholder,
            onGetFooterMessage: (itemCount: number, searchText: string) => getFooterMessage(
                itemCount,
                searchText,
                SharedResources.ShowingSingleProject,
                SharedResources.ShowingMultipleProjects,
                SharedResources.FoundSingleProject,
                SharedResources.FoundMultipleProjects),
            onRefineItems: filterItems,
            calloutProps: {
                title: SharedResources.RefineSearchTermText,
                content: SharedResources.ProjectFilterCalloutContent
            }
        }),

    [SearchConstants.WikiFacet]:
        (items: SearchPickList.ISearchPickListItem[], enabled: boolean): SearchPickList.ISearchPickListProps => ({
            name: SearchConstants.WikiFacet,
            items: items,
            allItemDisplayName: Resources.AllWikisLabel,
            allItemLabel: Resources.AllWikisLabel,
            width: 160,
            enabled: enabled,
            displayName: Resources.WikiFiltersDisplayLabel,
            searchTextPlaceholder: Resources.FindWikiPlaceholder,
            onGetFooterMessage: (itemCount: number, searchText: string) => getFooterMessage(
                itemCount,
                searchText,
                Resources.ShowingSingleWiki,
                Resources.ShowingMultipleWikis,
                Resources.FoundSingleWiki,
                Resources.FoundMultipleWikis),
            onRefineItems: filterItems,
            calloutProps: {
                title: SharedResources.RefineSearchTermText,
                content: Resources.WikiFilterCalloutContent
            }
        })
};
